<template>
  <div class="xfield form-field">
    <slot name="label">
      <label for="nationalIdNumber" class="cuscd-form-field-label"
        >{{ nationalIdNumberLabel
        }}<i
          class="required"
          v-if="schema.label && schema.required && schema.showRequiredIndicator"
          >*</i
        ></label
      >
    </slot>
    <div class="input-wrapper">
      <input
        v-bind="$attrs"
        ref="inputRef"
        type="text"
        name="nationalIdNumber"
        id="nationalIdNumber"
        pattern="[0-9\-X]+"
        inputmode="numeric"
        autocomplete="off"
        :disabled="isDisabled"
        @keyup="handleKeyUp"
        @keydown="validateSsnInput"
        :value="displayValue"
        @focus="isFocused = true"
        @blur="handleBlur"
        @input="handleInput($event)"
        class="text-input"
        :class="[
          'form-control',
          {
            'is-invalid': schema.touched && !schema.valid,
          },
        ]"
      />
      <button
        v-if="showNationalIdNumberToggle"
        type="button"
        @click="toggleNationalIdNumber"
        class="eye-btn"
        aria-label="Toggle SSN visibility"
      >
        <font-awesome-icon class="toggle" :icon="['fas', toggleIcon]" />
      </button>
    </div>
    <div
      class="inline-checkbox-container"
      v-if="showItinIndicator"
      :disabled="isDisabled"
    >
      <input
        type="checkbox"
        id="itinIndicator"
        v-model="itinIndicator"
        aria-label="Toggle ITIN checkbox"
        @change="
          $emit(
            'update:itinIndicator',
            ($event.target as HTMLInputElement).checked
          )
        "
      />
      <!-- The 'for' attribute links the label to the input ID for accessibility -->
      <label for="itinIndicator">Indicates ITIN</label>
    </div>
    <ul
      v-if="errors && errors.length >= 1 && !disabled"
      class="invalid-feedback"
      :class="{
        multiple: errors && errors.length > 1,
      }"
    >
      <li v-for="(error, index) in errors" :key="index">
        {{ error }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, toRef, onMounted } from "vue";
import * as yup from "yup";
import { useField } from "vee-validate";
import useValidation from "@/use/validation";
import type { FieldSchema } from "@/models/form";
import { usePermissionsAccess } from "@/composables/usePermissionsAccess";
import { Permissions, PermissionLevels } from "@/models/permissions";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
  NationalIdNumberTypeIds,
  NationalIdNumberTypeDefinitions,
} from "@/types/application-details";

const permissionsAccess = usePermissionsAccess();

const props = withDefaults(
  defineProps<{
    nationalIdNumberTypeId?: number;
    modelValue?: string;
    disabled: boolean;
    rules?: yup.Schema;
    schema: FieldSchema;
    fieldErrors?: string[];
    permissionIds: Permissions[];
    permissionLevel: number;
    showItinIndicator?: boolean;
  }>(),
  {
    permissionIds: () => [],
    permissionLevel: PermissionLevels.None,
    showItinIndicator: false,
    nationalIdNumberTypeId: 0,
  }
);
const modelValue = toRef(props, "modelValue");

const emit = defineEmits<{
  (e: "update:model-value", value: string | undefined): void;
  (e: "update:itinIndicator", value: boolean): void;
  (e: "update:valid", value: boolean | undefined): void;
  (e: "update:touched", value: boolean | undefined): void;
  (e: "update:errors", value: string[] | undefined[] | undefined | null): void;
}>();

const { isNationalIdNumberMasked } = useValidation();

const isMasked = ref<boolean>(true);
const isFocused = ref<boolean>(false);
const showNationalIdNumberToggle = ref<boolean>(false);
const itinIndicator = ref<boolean>(false);

const toggleIcon = computed<string>(() =>
  isMasked.value ? "eye" : "eye-slash"
);

const canUpdate = computed<boolean>(() => {
  const permissionIdsValue = [Permissions.ApplicantSSN];

  return permissionsAccess.hasPermission(
    permissionIdsValue,
    PermissionLevels.Update
  );
});

const isVisible = computed(() => {
  const permissionIdsValue = props.permissionIds || [];
  return (
    permissionIdsValue.length === 0 ||
    permissionsAccess.hasPermission(permissionIdsValue, props.permissionLevel)
  );
});

const isDisabled = computed<boolean>(() => {
  return props.disabled || !isVisible.value || !canUpdate.value;
});

const displayValue = computed<string | undefined | unknown>(() => {
  if (!canUpdate.value) {
    return props.modelValue;
  }

  const maskedPart = "XXX-XX-";
  const cleanedValue = props.modelValue?.replace(/[^0-9]/g, "");

  if (props.disabled) {
    if (cleanedValue) {
      if (cleanedValue.length === 4) {
        return maskedPart + cleanedValue;
      }

      if (cleanedValue.length === 9) {
        const visiblePart = cleanedValue.slice(-4);
        return maskedPart + visiblePart;
      }
    }
  }

  if (isNationalIdNumberMasked(props.modelValue || "") || !isFocused.value) {
    return props.modelValue;
  }

  return value.value;
});

const rawValue = computed<string | undefined>(() => {
  const cleanedValue = props.modelValue?.replace(/[^0-9]/g, "");
  return cleanedValue ? cleanedValue : undefined;
});

const maskedValue = computed<string>(() => {
  const cleanedValue = props.modelValue?.replace(/[^0-9]/g, "");
  return cleanedValue ? `XXX-XX-${cleanedValue.slice(-4)}` : "";
});

const nationalIdNumberLabel = computed<string>(() => {
  return props.nationalIdNumberTypeId == NationalIdNumberTypeIds.ITIN
    ? NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.ITIN]?.name
    : NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.SSN]?.name;
});

const { handleChange, value, errors, validate, resetField, setValue } =
  useField(
    props.schema.name || "nationalIdNumber",
    props.rules || yup.string()
  );

function formatNationalIdNumber(nationalIdNumber: string): string | undefined {
  if (isNationalIdNumberMasked(nationalIdNumber)) {
    return nationalIdNumber;
  }
  // Automatically format the SSN as the user types
  if (nationalIdNumber && !isNationalIdNumberMasked(nationalIdNumber)) {
    // Remove non-numeric characters
    let digits = nationalIdNumber.replace(/\D/g, "");

    // Limit to 9 digits (standard SSN length)
    if (digits.length > 9) {
      digits = digits.slice(0, 9);
    }

    let formattedValue = digits;
    // Apply SSN format: XXX-XX-XXXX
    if (digits.length > 5) {
      formattedValue = `${digits.slice(0, 3)}-${digits.slice(
        3,
        5
      )}-${digits.slice(5)}`;
    } else if (digits.length > 3) {
      formattedValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      formattedValue = digits;
    }

    return formattedValue;
  }

  return undefined;
}

function formatInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  const cleanValue = `${value}`.replace(/[^0-9]/g, "");

  if (!value) {
    setValue(undefined);
    return;
  }

  setValue(formatNationalIdNumber(cleanValue));
}

function handleBlur() {
  isFocused.value = false;
  emit("update:touched", true);
  validate();
}

function handleKeyUp(event: Event) {
  formatInput(event);
}

function toggleNationalIdNumber() {
  //#TODO call endpoint to retrieve the unmasked one
  isMasked.value = !isMasked.value;
}

const ignoreKeys: string[] = [
  "Backspace",
  "ArrowLeft",
  "ArrowRight",
  "Tab",
  "Enter",
  "Delete",
  "Shift",
  "CapsLock",
  "Home",
  "End",
];

function validateSsnInput(event: any) {
  // 1. Allow structural, control, and navigation keys (Backspace, Delete, Tab, Escape, Enter, Arrow keys)
  const isControlKey = ignoreKeys.indexOf(event.key) >= 0;

  // 2. Allow keyboard shortcuts (Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X)
  const isShortcut =
    (event.ctrlKey || event.metaKey) &&
    ["a", "c", "v", "x"].includes(event.key.toLowerCase());

  if (isControlKey || isShortcut) {
    return;
  }
  // 3. Block the key if it is not a digit (0-9) or a dash
  const isNumberOrHyphen = /^[0-9-]$/.test(event.key);

  // 4. Do not allow more than 9 numbers
  const cleanedValue = event.target.value?.replace(/[^0-9]/g, "");

  if (!isNumberOrHyphen || cleanedValue.length >= 9) {
    event.preventDefault();
  }
}

function handleInput(event: Event) {
  // Handle edge cases where input is autofill or pasted
  const element = event.target as HTMLInputElement;

  // Global replacement of everything that isn't a number
  element.value = element.value.replace(/[^0-9]/g, "");
  const value = element?.value;

  // format it if there are 9 digits
  if (value && value.length === 9) {
    handleChange(formatNationalIdNumber(value));
  } else {
    handleChange(value);
  }
}

watch(modelValue, (newValue, oldValue) => {
  if (
    (oldValue === "" || oldValue === null || oldValue === undefined) &&
    (newValue === "" || newValue === null || newValue === undefined)
  ) {
    setValue(null, false);
    return;
  }
  if (newValue === "" || newValue === null || newValue === undefined) {
    setValue(null);
  } else {
    setValue(value.value, false);
  }
});

watch(value, () => {
  if (value.value && typeof value.value === "string") {
    emit("update:model-value", formatNationalIdNumber(value.value));
  } else {
    emit("update:model-value", undefined);
  }
});
// using external errors, yup validation
watch(
  () => errors.value,
  () => {
    emit("update:errors", errors.value ?? ([] as string[]));
    emit("update:valid", errors.value?.length === 0);
  },
  {
    deep: true,
  }
);
onMounted(() => {
  if (props.modelValue) {
    setValue(value.value, false);
  }
});
defineExpose({
  value,
  errors,
  maskedValue,
  rawValue,
  isNationalIdNumberMasked,
});
</script>

<style scoped src="@/assets/css/baseformfield.scss" lang="scss"></style>
<style scoped>
.input-wrapper {
  position: relative;
  width: 100%;
}
.input-wrapper input {
  width: 100%;
  padding: 10px 40px 10px 12px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  height: 46px;
}

.eye-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
}

.eye-btn:hover {
  color: #333;
}

.inline-checkbox-container {
  display: flex;
}

.inline-checkbox-container label {
  padding-bottom: 0;
  font-weight: 400;
  font-size: 12px;
  padding-left: 2px;
}
</style>
